import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useSyncExternalStore } from "react";

import { ButtonLink } from "~/components/ui/button";
import { createNoIndexMeta } from "~/lib/shared/meta";

type FrameState = "unknown" | "framed" | "standalone";

const subscribeToNothing = (): (() => void) => () => undefined;

function readFrameState(): FrameState {
  return window.self !== window.top ? "framed" : "standalone";
}

// Only the browser knows whether it sits inside a frame. The server and the
// hydration pass both render "unknown", then the client re-renders.
function useFrameState(): FrameState {
  return useSyncExternalStore<FrameState>(
    subscribeToNothing,
    readFrameState,
    () => "unknown",
  );
}

export const Route = createFileRoute("/coach/$")({
  staticData: { title: "Dream Coach Match has moved" },
  head: () =>
    createNoIndexMeta(
      "Dream Coach Match has moved | Dream Coach Match",
      "Coaches now use Dream Coach Match at /my-coaching.",
    ),
  component: CoachMoved,
});

/**
 * Old `/coach/*` links land here. GoHighLevel frames DCM pages, and a coach
 * sign-in inside that frame usually fails (third-party cookies, Google
 * sign-in), so a framed visit gets a link that opens the app in its own tab.
 * Everyone else goes straight to `/my-coaching`.
 */
function CoachMoved() {
  const frameState = useFrameState();
  const navigate = useNavigate();

  useEffect(() => {
    if (frameState === "standalone") {
      void navigate({ to: "/my-coaching", replace: true });
    }
  }, [frameState, navigate]);

  if (frameState !== "framed") return null;

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <h1 className="text-text-primary font-heading text-2xl font-medium">
          Dream Coach Match has moved
        </h1>
        <p className="text-text-secondary text-sm">
          Your coaching workspace now lives in the main app. Open it in a new
          tab to continue.
        </p>
        <ButtonLink
          variant="primary"
          href="/my-coaching"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open it in a new tab
        </ButtonLink>
      </div>
    </div>
  );
}
